import React, { useState, useEffect } from 'react';
import { Shield, Phone, Wallet, CreditCard, FileText, QrCode, Mail, Image, BookOpen, ExternalLink, Download } from 'lucide-react';

const RookieCheatSheet = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/member/onboarding/cheatsheet-resources`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.ok) {
        setResources(data.resources);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      communication: Phone,
      payments: Wallet,
      tools: FileText,
      templates: BookOpen
    };
    return icons[category] || FileText;
  };

  const getIconComponent = (iconName) => {
    const icons = {
      phone: Phone,
      voicemail: Phone,
      wallet: Wallet,
      payment: CreditCard,
      exchange: CreditCard,
      template: FileText,
      qrcode: QrCode,
      design: Image,
      image: Image,
      email: Mail,
      library: BookOpen,
      school: BookOpen
    };
    return icons[iconName] || FileText;
  };

  const categories = [
    { id: 'all', name: 'All Resources', icon: Shield },
    { id: 'communication', name: 'Communication', icon: Phone },
    { id: 'payments', name: 'Payments & Wallets', icon: Wallet },
    { id: 'tools', name: 'Tools & Services', icon: FileText },
    { id: 'templates', name: 'Templates & Training', icon: BookOpen }
  ];

  const filteredResources = filter === 'all' 
    ? resources 
    : resources.filter(r => r.category === filter);

  const featuredResources = resources.filter(r => r.is_featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your cheat sheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">🚀 Rookie Cheat Sheet</h1>
              <p className="text-indigo-100 text-lg">
                Your go-to resources for building your list fast—tools, wallets, and templates
              </p>
            </div>
            <Download className="h-12 w-12 opacity-50" />
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Rookie Level Access:</strong> All leads are captured directly in your BBC Quick List Back Office. 
                Download as CSV anytime. Advanced email tools (AWeber, Mailgun) unlock at <strong>Enterprise level</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Featured Resources */}
        {featuredResources.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              ⭐ Must-Have Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredResources.map(resource => {
                const Icon = getIconComponent(resource.icon);
                return (
                  <a
                    key={resource.id}
                    href={resource.url}
                    target={resource.url.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className="bg-white border-2 border-indigo-200 rounded-lg p-6 hover:shadow-lg hover:border-indigo-400 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="bg-indigo-100 rounded-lg p-3">
                        <Icon className="h-6 w-6 text-indigo-600" />
                      </div>
                      <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-indigo-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{resource.name}</h3>
                    <p className="text-sm text-gray-600">{resource.description}</p>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === category.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* All Resources */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {filter === 'all' ? 'All Resources' : categories.find(c => c.id === filter)?.name}
            </h2>
            <div className="space-y-4">
              {filteredResources.map(resource => {
                const Icon = getIconComponent(resource.icon);
                const CategoryIcon = getCategoryIcon(resource.category);
                return (
                  <div
                    key={resource.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <Icon className="h-6 w-6 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{resource.name}</h3>
                            {resource.is_featured && (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <CategoryIcon className="h-3 w-3" />
                            <span className="capitalize">{resource.category}</span>
                          </div>
                        </div>
                      </div>
                      <a
                        href={resource.url}
                        target={resource.url.startsWith('http') ? '_blank' : '_self'}
                        rel="noopener noreferrer"
                        className="ml-4 flex items-center text-indigo-600 hover:text-indigo-800 font-medium text-sm whitespace-nowrap"
                      >
                        Open
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredResources.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No resources found in this category</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700">
            💡 <strong>Pro Tip:</strong> Bookmark this page for quick access to all your essential tools.
            As you progress through Boot Camp, we'll unlock more advanced resources.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RookieCheatSheet;
